import * as React from 'react';
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../utils/PluginTemplates";
import {lazyInject} from "@athena-portal/core";
import {
    FalconNavigatorHeader,
    ICustomerPortalService,
    Party,
    Policy,
    productHelper,
    Subscription,
    Types,
    User
} from "@falcon/services";
import FalconFooter from "../../../components/navigation/FalconFooter";
import FalconHeader from "../../../components/navigation/FalconHeader";
import {Link} from "react-router-dom";
import {map} from "rxjs/operators";
import {PersonalDetailsWidget} from "../../../components/accountDetails/PersonalDetailsWidget";
import {ContactDetailsWidget} from "../../../components/accountDetails/ContactDetailsWidget";
import {PanelDividerLine} from "../../../components/PanelDividerLine";
import {AddressDetailsWidget} from "../../../components/accountDetails/AddressDetailsWidget";
import {ChangePasswordWidget} from "../../../components/accountDetails/ChangePasswordWidget";

interface IPolicySummaryPageParams {
    policyNumber: string;
    [key: string]: string | undefined;
}

interface IState {
    party: Party | undefined;
    partyLoaded: boolean;
    subscriptionLoaded: boolean;
    subscription?: Subscription | null;
    policies?: Policy[];
    policiesLoaded: boolean;
    user: User | undefined;
    userLoaded: boolean;
}

@authorize
export class AccountDetailsPagePlain extends React.Component<IWithRouterProps<IPolicySummaryPageParams>, IState> {

    private pluginTemplates = new PluginTemplates();

    @lazyInject(Types.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    constructor(props: IWithRouterProps<IPolicySummaryPageParams>) {
        super(props);

        this.state = {
            party: undefined,
            partyLoaded: false,
            user: undefined,
            userLoaded: false,
            policies: [],
            policiesLoaded: false,
            subscription: null,
            subscriptionLoaded: false
        }
    }

    public componentDidMount() {
        {window.scrollTo(0, 0)}

        this.customerPortalService.getSubscription().subscribe((subscription: Subscription) => {
            this.setState({
                subscription,
                subscriptionLoaded: true
            });
        });

        this.customerPortalService.getPolicies().pipe(
            map((policies: Policy[]) => policies.filter((policy: Policy) => !productHelper.isWolfMotor(policy.interaction)))
        ).subscribe((policies: Policy[]) => {
            this.setState({
                policies,
                policiesLoaded: true
            });
        });

        this.customerPortalService.getUserById().subscribe((user: User) => {
            this.setState({
                user,
                userLoaded: true
            });
        });

        this.customerPortalService.getProfile().subscribe((output: Party) => {
            this.setState({
                party: output,
                partyLoaded: true
            })
        });

        this.pluginTemplates.setPageTitle("Your account details");
    }

    public render() {

        const {
            party,
            partyLoaded,
            policies,
            policiesLoaded,
            subscription,
            subscriptionLoaded,
            userLoaded,
            user

        } = this.state;
        const loading: boolean = !(partyLoaded && policiesLoaded && subscriptionLoaded && userLoaded);

        if (loading) {
            return  <Overlay show={true}/>;
        } else if (party && subscription && policies && user) {
            return (
                <React.Fragment>
                    <FalconHeader/>

                    <div className="plugin">
                        <FalconNavigatorHeader
                            back="/dashboard/monthlySubscription"
                            title="Account details"
                        />
                        <div className="plugin plugin-panel inner-container inner-content mb-3">
                            Heads up – <b>changing your account details could also change your price</b>
                        </div>
                        <div className="plugin plugin-panel inner-container inner-content text-center">
                            <div className="panel-detail-output-entity account-details-title p-0">
                                <div>
                                    <img className="account-details-icon" alt="make-changes" src="/img/account-details.png"/>
                                </div>
                            </div>
                            <div>
                                <PersonalDetailsWidget party={party}/>
                                <PanelDividerLine/>
                                <ContactDetailsWidget party={party} hasIdp={party.hasIdp}/>
                                <PanelDividerLine/>
                                <AddressDetailsWidget addressChangeUrl="/journey/show/product/FlowSubscription/process/mtaChangeAddress/changeAddressPresetPage/"
                                                      allowAddressChange={true}
                                                      futureMta={false}
                                                      coverLapsed={false}
                                                      fullAddress={policies[0].interaction.policyHolder.address.fullAddress}
                                                      coverCancelled={subscription.allPoliciesAreCancelled}
                                />
                                <PanelDividerLine/>
                                {this.state.user && !party.hasIdp && <ChangePasswordWidget/>}
                                <PanelDividerLine/>
                            </div>
                        </div>
                        {this.renderCancelSubscriptionButton()}

                        {this.pluginTemplates.renderBackLink("Back", false, "/dashboard/monthlySubscription")}
                        <Overlay show={loading}/>
                    </div>

                    <FalconFooter/>
                </React.Fragment>
            )
        } else {
            return <></>
        }
    }

    private renderCancelSubscriptionButton(): React.ReactNode {
        const rentersEnabled: boolean = process.env.REACT_APP_RENTERS_ENABLED ? JSON.parse(process.env.REACT_APP_RENTERS_ENABLED) : true;

        if (rentersEnabled) {
            if (this.state.subscription?.allPoliciesAreCancelled) {
                if (this.state.subscription?.cancellationUWFailure || this.state.subscription?.allMTCsAreFromSupportPortal) {
                    return <></>
                } else {
                    return (
                        <div className="form-group pt-4 px-3 px-md-5">
                            <Link to="/journey/show/product/FlowSubscription/process/mtaReinstateSubscription"
                                className="buttonLink"
                                id="reactivateSubscription">
                                <button type="button" className="btn btn-white-dashboard w-100">Reactivate subscription</button>
                            </Link>
                        </div>
                    )
                }
            } else {
                return (
                    <div className="form-group pt-4 px-3 px-md-5">
                        <Link to="/journey/show/product/FlowSubscription/process/mtc"
                            className="buttonLink"
                            id="cancelSubscription">
                            <button type="button" className="btn btn-white-dashboard w-100">Cancel subscription</button>
                        </Link>
                    </div>
                )
            }
        } else {
            return <></>
        }
    }
}

export const AccountDetailsPage = withRouter<{}, IPolicySummaryPageParams>(AccountDetailsPagePlain)
